import { LoadingButton } from '@mui/lab';
import React, { useEffect, useRef, useState } from 'react'
import DialogReuse from '../../pages/Editor/ReuseComponents/DialogReuse';
import { Autocomplete, Box, Button, Chip, createFilterOptions, FormControl, FormHelperText, InputLabel, MenuItem, Select, TextField, useTheme } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import transformSubjectConstraintsToExpression from '../../utils/transformSubjectConstraintsToExpression';
import findChildrenNode from '../../utils/findChildrenNode';
import { useReactFlow } from '@xyflow/react';
import { useSelector } from 'react-redux';
import { getSubjectSchema } from '../../redux/subjectSchemaSlice';
import { dataTypes } from '../../utils/configDataTypes';
import { createLDAPValidator } from '../../utils/ldapTypeValidator';

const filter = createFilterOptions();
const singleValueSuggestions = ['NULL', 'Empty String'];

const normalizeSingleConstraintValue = (value) => {
    if (value === 'Empty String') {
        return '';
    }
    return value;
};

const getOperatorConstraint = (operator) => {
    if (!operator) return '';
    if (typeof operator === 'string') return operator;
    return operator.constraint ?? '';
};

const getOperatorLabel = (operator) => {
    if (!operator) return '';
    if (typeof operator === 'string') return operator;
    return operator.label ?? operator.condition ?? operator.constraint ?? '';
};

const getValidConstraintOperators = (operators = []) => {
    return operators.filter((operator) => {
        if (typeof operator === 'string') {
            return operator !== '';
        }
        return operator?.constraint !== null && operator?.constraint !== undefined && operator?.constraint !== '';
    });
};

const findConstraintOperatorConfig = (operator, operators = []) => {
    const selectedConstraint = getOperatorConstraint(operator);
    if (!selectedConstraint) return null;

    const matchedOperator = getValidConstraintOperators(operators).find(
        (item) => getOperatorConstraint(item) === selectedConstraint
    );

    if (matchedOperator) {
        return matchedOperator;
    }

    for (const dataType of dataTypes) {
        const fallbackMatch = getValidConstraintOperators(dataType?.operators || []).find(
            (item) => getOperatorConstraint(item) === selectedConstraint
        );

        if (fallbackMatch) {
            return fallbackMatch;
        }
    }

    return null;
};

const isMultipleConstraintOperator = (operator, operators = []) => {
    const matchedOperator = findConstraintOperatorConfig(operator, operators);

    if (matchedOperator && typeof matchedOperator === 'object') {
        return !!matchedOperator.multiple;
    }

    // Also check for IN/NOT IN operators (these always accept array values)
    const operatorConstraint = getOperatorConstraint(operator);
    const normalizedOperator = String(operatorConstraint).toUpperCase();
    if (normalizedOperator === 'IN' || normalizedOperator === 'NOT IN') {
        return true;
    }

    return false;
};

const isInOperator = (operator) => {
    const operatorConstraint = getOperatorConstraint(operator);
    const normalizedOperator = String(operatorConstraint).toUpperCase();
    return normalizedOperator === 'IN' || normalizedOperator === 'NOT IN';
};

const isOperatorConstraintSupported = (operators = [], operator) => {
    const selectedConstraint = getOperatorConstraint(operator);
    if (!selectedConstraint) return false;

    return getValidConstraintOperators(operators).some((item) => getOperatorConstraint(item) === selectedConstraint);
};

function SubjectConstraintDialog(props) {
    const {
        open,
        setOpen,
        nodes,
        setNodes,
        selectedNode,
        subjectConstraints,
        editingOuterIndex,
        edges,
        setUpdateProperties
    } = props;

    const nodesRef = useRef(nodes);

    const theme = useTheme();

    const { getNode } = useReactFlow();

    const [subjectAttributes, setSubjectAttributes] = useState([]);
    const [subjectConstraintChild, setSubjectConstraintChild] = useState([]);

    const [valueErrors, setValueErrors] = useState({});

    const [disabled, setDisabled] = useState(true);

    const subjectSchema = useSelector(getSubjectSchema);

    const [inputValues, setInputValues] = useState({}); // Multiple Value per row
    const [inputErrors, setInputErrors] = useState({});

    const setIndexedState = (setter, index, value) => {
        setter((prev) => {
            const next = { ...prev };

            if (value === null || value === undefined || value === '') {
                delete next[index];
            } else {
                next[index] = value;
            }

            return next;
        });
    };

    const setValueErrorByIndex = (index, value) => setIndexedState(setValueErrors, index, value);
    const setInputErrorByIndex = (index, value) => setIndexedState(setInputErrors, index, value);
    const setInputValueByIndex = (index, value) => setIndexedState(setInputValues, index, value);

    const handleClose = () => {
        setOpen(false)
    };

    useEffect(() => {

        const handleGetSubjectAttribute = async () => {

            const getSubjectSchema = subjectSchema || [];
            // console.log('subject constraint policy', getSubjectSchema)

            const selectAttribute = getSubjectSchema.map(attr => {
                const attrType = attr.attributeType.toLowerCase();
                const foundType = dataTypes.find(dt => {
                    const lowercasedAttrType = dt.similar_type.map(type => type.toLowerCase());
                    if (lowercasedAttrType.includes(attrType)) {
                        return true;
                    } else {
                        return false;
                    }
                });
                return {
                    ...attr,
                    type: foundType ? foundType.type : 'string', // Default เป็น string ถ้าไม่พบ
                    operators: foundType ? getValidConstraintOperators(foundType.operators) : []
                }
            });
            // console.log('selectAttribute', selectAttribute)
            setSubjectAttributes(selectAttribute);

        };

        if (open) {
            handleGetSubjectAttribute();
        }

    }, [open, subjectSchema])

    useEffect(() => {
        nodesRef.current = nodes;
    }, [nodes])

    useEffect(() => {
        const initial = {
            conjunction: '',
            child: [
                {
                    l_operand: null,
                    operator: '',
                    r_operand: '',
                    conjunction: ''
                }
            ]
        };
        if (editingOuterIndex !== null && open) {
            setSubjectConstraintChild(subjectConstraints[editingOuterIndex]?.child);
        } else {
            setSubjectConstraintChild(initial.child);
        }

        setValueErrors({});
        setInputErrors({});
        setInputValues({});
    }, [editingOuterIndex, open])

    useEffect(() => {
        if (open) {
            const isSubjectConstraintChildInvalid = (child) => {
                return child.some((obj, index) => {
                    const allowEmptyString = supportsEmptyString(obj?.l_operand?.attributeType);
                    const hasInvalidArrayValue = Array.isArray(obj.r_operand) && obj.r_operand.length === 0;
                    const isBlankScalarValue = !Array.isArray(obj.r_operand) && (
                        obj.r_operand === null ||
                        obj.r_operand === undefined ||
                        obj.r_operand === ''
                    );
                    const isExplicitEmptyString = !Array.isArray(obj.r_operand) && obj.r_operand === 'Empty String';
                    const hasInvalidScalarValue = isBlankScalarValue || (isExplicitEmptyString && !allowEmptyString);
                    const hasInputError = !!valueErrors[index] || !!inputErrors[index] || !!obj?.attr_error_text || !!obj?.operator_error_text || !!obj?.data_type_error_text;

                    return !obj.l_operand || obj.operator === '' || hasInvalidArrayValue || hasInvalidScalarValue || hasInputError;
                });
            }
            if (subjectConstraintChild.length > 0) {
                setDisabled(isSubjectConstraintChildInvalid(subjectConstraintChild))
            }
        }
    }, [subjectConstraintChild, open, valueErrors, inputErrors])

    const handleChange = (index, field, value) => {
        setSubjectConstraintChild(prev =>
            prev.map((cond, i) => {
                if (i !== index) return cond;

                let isValidInput = true;

                const normalizedValue = field === 'operator' ? getOperatorConstraint(value) : value;

                let updated = {
                    ...cond,
                    [field]: normalizedValue,
                };

                if (field === 'l_operand') {
                    delete updated.attr_error_text;
                    setValueErrorByIndex(index, null);
                    setInputErrorByIndex(index, null);
                    setInputValueByIndex(index, '');
                    return {
                        ...updated,
                        operator: '',
                        r_operand: '',
                    };
                }
                if (field === 'operator') {
                    delete updated.operator_error_text;
                    setValueErrorByIndex(index, null);
                    setInputErrorByIndex(index, null);
                    setInputValueByIndex(index, '');

                    const nextIsMultiple = isMultipleConstraintOperator(normalizedValue, cond?.l_operand?.operators) || isInOperator(normalizedValue);
                    const previousOperand = cond?.r_operand;
                    let normalizedOperand = previousOperand;

                    if (nextIsMultiple) {
                        if (Array.isArray(previousOperand)) {
                            normalizedOperand = previousOperand;
                        } else if (previousOperand === null || previousOperand === undefined || previousOperand === '') {
                            normalizedOperand = [];
                        } else {
                            normalizedOperand = [previousOperand];
                        }
                    } else if (Array.isArray(previousOperand)) {
                        normalizedOperand = previousOperand.length > 0 ? previousOperand[0] : '';
                    }

                    return {
                        ...updated,
                        r_operand: normalizedOperand,
                    };
                }
                if (field === 'r_operand') {
                    if (Array.isArray(value)) {
                        delete updated.data_type_error_text;
                        return {
                            ...updated,
                            r_operand: value,
                        };
                    }

                    if (value === '') {
                        setValueErrorByIndex(index, null);
                        setInputErrorByIndex(index, null);
                        delete updated.data_type_error_text;
                        return {
                            ...updated,
                            r_operand: '',
                        };
                    }

                    const normalizedInputValue = normalizeSingleConstraintValue(value);
                    let newValue = normalizedInputValue;
                    const typeValidator = createLDAPValidator(updated?.l_operand?.attributeType);
                    const validateResult = typeValidator.validate(newValue);
                    if (!validateResult.valid) {
                        setValueErrorByIndex(index, validateResult.error);
                        isValidInput = false;
                    } else {
                        newValue = validateResult.parsed;
                        setValueErrorByIndex(index, null);
                        setInputErrorByIndex(index, null);
                    }
                    delete updated.data_type_error_text;
                    return {
                        ...updated,
                        r_operand: validateResult.valid
                            ? (value === 'Empty String' ? 'Empty String' : newValue)
                            : value,
                    };
                }

                return updated;
            })
        );
    };

    const handleAutoCompleteChange = (childIndex, event, newValue, reason, operandType) => { // AutoComplete Multiple Value
        if (reason === 'clear') {
            handleChange(childIndex, "r_operand", []);
            setInputValueByIndex(childIndex, '');
            setInputErrorByIndex(childIndex, null);
            setValueErrorByIndex(childIndex, null);
            return;
        }

        handleChange(childIndex, "r_operand", newValue);

        const attributeType = subjectConstraintChild[childIndex]?.l_operand?.attributeType;

        if (newValue.some(val => hasSpecialCharacters(val))) {
            setValueErrorByIndex(childIndex, "Special characters are not allowed");
        } else if (newValue.some(val => !isValidType(val, operandType, attributeType))) {
            setValueErrorByIndex(childIndex, `Invalid ${attributeType || operandType || 'string'} value`);
        } else {
            setValueErrorByIndex(childIndex, null);
        }

    };

    const handleDateChange = (index, newValue) => {
        if (newValue && dayjs(newValue).isValid()) {
            const formatted = dayjs(newValue).format('YYYY-MM-DD');
            setValueErrorByIndex(index, null);
            setInputErrorByIndex(index, null);
            setSubjectConstraintChild(prev =>
                prev.map((cond, i) => {
                    if (i !== index) return cond;

                    const updated = {
                        ...cond,
                        r_operand: formatted,
                    };

                    delete updated.data_type_error_text;

                    return updated;
                })
            );
        } else {
            setValueErrorByIndex(index, null);
            setInputErrorByIndex(index, null);
            setSubjectConstraintChild(prev =>
                prev.map((cond, i) => {
                    if (i !== index) return cond;

                    const updated = {
                        ...cond,
                        r_operand: '',
                    };

                    delete updated.data_type_error_text;

                    return updated;
                })
            );
        }
    };

    const hasSpecialCharacters = (str) => /[^\p{L}\p{N}\s.-]/u.test(str);

    const isSQLInjection = (str) => {
        const sqlInjectionPattern = /(\b(SELECT|UNION|INSERT|UPDATE|DELETE|DROP|ALTER|EXEC|CREATE|TRUNCATE|RENAME|MERGE|DECLARE|GRANT|REVOKE)\b|(--|#|;|\'|\"|\/\*|\*\/)|\bOR\b(?![a-zA-Z0-9-]*\.[a-zA-Z]{2,})|\bAND\b(?![a-zA-Z0-9-]*\.[a-zA-Z]{2,}))/gi;
        if (sqlInjectionPattern.test(str)) {
            return true; // พบคำสั่ง SQL ที่เป็นอันตราย -> ถือว่า "มีปัญหา" หรือ รูปแบบ email ไม่ถูกต้อง
        }
        return false;
    }

    const isIntegerAttributeType = (attributeType = '') => {
        return ['integer', 'bigint', 'smallint', 'tinyint'].includes(String(attributeType).toLowerCase());
    };

    const supportsEmptyString = (attributeType = '') => {
        if (!attributeType) return false;

        try {
            const typeValidator = createLDAPValidator(attributeType);
            return typeValidator.validate('').valid;
        } catch {
            return false;
        }
    };

    const isValidType = (val, operandType, attributeType = '') => {
        const validateIntegerValue = (input) => /^-?\d+$/.test(String(input).trim());

        if (val === '') {
            return supportsEmptyString(attributeType);
        }

        if (isIntegerAttributeType(attributeType)) {
            return Array.isArray(val) ? val.every(validateIntegerValue) : validateIntegerValue(val);
        }

        if ((operandType || '').toLowerCase() === "number") {
            return Array.isArray(val)
                ? val.every((v) => String(v).trim() !== '' && !isNaN(Number(v)))
                : String(val).trim() !== '' && !isNaN(Number(val));
        }

        if (attributeType) {
            try {
                // If attributeType is 'array' or similar array type but value is NOT an array (it's a string),
                // just treat it as a valid string element - don't validate with array validator
                const normalizedAttrType = String(attributeType).toLowerCase();
                if ((normalizedAttrType === 'array' || normalizedAttrType.includes('array')) && !Array.isArray(val)) {
                    // String values are always valid as array elements
                    return true;
                }

                const typeValidator = createLDAPValidator(attributeType);
                return Array.isArray(val)
                    ? val.every((item) => typeValidator.validate(item).valid)
                    : typeValidator.validate(val).valid;
            } catch {
                return true;
            }
        }

        return true;
    };

    const validateSubjectConstraints = (constraints = []) => {
        const resolvedSubjectSchema = Array.isArray(subjectSchema) ? subjectSchema : [];

        return constraints.map((constraint) => {
            const validatedChild = (constraint?.child || []).map((sub) => {
                const { attr_error_text, data_type_error_text, operator_error_text, ...sub_without_error } = sub;

                if (!sub_without_error?.l_operand?.name) {
                    return sub_without_error;
                }

                const matchedAttribute = resolvedSubjectSchema.find((attr) => attr.name === sub_without_error.l_operand.name);

                if (!matchedAttribute) {
                    return {
                        ...sub_without_error,
                        attr_error_text: "Attribute no longer available. Please select another."
                    };
                }

                const newDataType = matchedAttribute.attributeType;
                const normalizedDataType = String(newDataType || '').toLowerCase();
                const newGroupType = dataTypes.find((dt) =>
                    dt.similar_type.some((type) => String(type).toLowerCase() === normalizedDataType)
                );

                let validatedSub = {
                    ...sub_without_error,
                    l_operand: {
                        ...sub_without_error.l_operand,
                        attributeType: newDataType,
                        operators: newGroupType ? newGroupType.operators : [],
                        type: newGroupType ? newGroupType.type : undefined
                    }
                };

                const typeValidator = createLDAPValidator(newDataType);
                const normalizedRightOperand = Array.isArray(sub_without_error.r_operand)
                    ? sub_without_error.r_operand
                    : normalizeSingleConstraintValue(sub_without_error.r_operand);

                const normalizedNewDataType = String(newDataType).toLowerCase();
                const isArrayTypeWithStringValue = (normalizedNewDataType === 'array' || normalizedNewDataType.includes('array'))
                    && !Array.isArray(normalizedRightOperand)
                    && normalizedRightOperand !== '';

                const normalizedOperator = String(sub_without_error.operator).toLowerCase();
                const isArrayIncludesOperator = (normalizedNewDataType === 'array' || normalizedNewDataType.includes('array'))
                    && (normalizedOperator === 'includes' || normalizedOperator.includes('includes'));

                // Check for IN/NOT IN operators (these accept array values regardless of l_operand type)
                const isInNotInOperator = isInOperator(sub_without_error.operator);

                let validateResult;
                if (isArrayTypeWithStringValue) {
                    // For array type with string value, just accept it as a valid element
                    validateResult = {
                        valid: true,
                        parsed: normalizedRightOperand,
                        error: null
                    };
                } else if (isArrayIncludesOperator && Array.isArray(normalizedRightOperand)) {
                    // For array type with array value, accept each element without strict array validation
                    validateResult = {
                        valid: true,
                        parsed: normalizedRightOperand,
                        error: null
                    };
                } else if (isInNotInOperator && Array.isArray(normalizedRightOperand)) {
                    // For IN/NOT IN operators with array value, accept it without strict validation
                    validateResult = {
                        valid: true,
                        parsed: normalizedRightOperand,
                        error: null
                    };
                } else {
                    validateResult = typeValidator.validate(normalizedRightOperand);
                }

                const isOperatorMismatch = !isArrayIncludesOperator && !isInNotInOperator && !isOperatorConstraintSupported(validatedSub.l_operand.operators, sub_without_error.operator);

                if (isOperatorMismatch) {
                    validatedSub = {
                        ...validatedSub,
                        operator_error_text: "Operator for attribute type invalid."
                    };
                }

                // For array type with string/scalar value, bypass validation error
                if (isArrayTypeWithStringValue || (!validateResult.valid && (isArrayIncludesOperator || isInNotInOperator))) {
                    validatedSub = {
                        ...validatedSub,
                        r_operand: normalizedRightOperand
                    };
                } else if (!validateResult.valid) {
                    validatedSub = {
                        ...validatedSub,
                        data_type_error_text: validateResult.error
                    };
                } else {
                    validatedSub = {
                        ...validatedSub,
                        r_operand: validateResult.parsed
                    };
                }

                return validatedSub;
            });

            const hasSubError = validatedChild.some((sub) => sub.attr_error_text || sub.data_type_error_text || sub.operator_error_text);

            if (hasSubError) {
                return {
                    ...constraint,
                    child: validatedChild,
                    error: true,
                };
            }

            const { error, ...constraint_without_error } = constraint;
            return {
                ...constraint_without_error,
                child: validatedChild
            };
        });
    };

    const applySubjectConstraintValidationToNode = (node, constraints) => {
        const validatedConstraints = validateSubjectConstraints(constraints);
        let newErrorText = Array.isArray(node?.data?.error_text)
            ? node.data.error_text.filter((err) => (err !== 'Invalid constraint.' && err !== 'Constraint invalid. Please correct it.'))
            : [];

        const hasConstraintError = validatedConstraints.some((constraint) => constraint.error === true);

        if (hasConstraintError) {
            newErrorText.push('Constraint invalid. Please correct it.');
        }

        return {
            ...node,
            data: {
                ...node.data,
                general_properties: {
                    ...node.data.general_properties,
                    subjectConstraints: validatedConstraints,
                    expression: transformSubjectConstraintsToExpression(validatedConstraints)
                },
                error: newErrorText.length > 0,
                error_text: newErrorText
            }
        };
    };

    const handleAddChild = (index) => {
        setValueErrors({});
        setInputErrors({});
        setInputValues({});

        setSubjectConstraintChild((prev) => {
            const newSubjectConstraints = prev.map((cond, i) => {
                if (i === index) {
                    return { ...cond, conjunction: "AND" };
                }
                return cond;
            });

            newSubjectConstraints.push({
                l_operand: null,
                operator: '',
                r_operand: '',
                conjunction: ''
            });

            return newSubjectConstraints;
        });
    };

    const handleRemoveChild = (index) => {
        setValueErrors({});
        setInputErrors({});
        setInputValues({});

        setSubjectConstraintChild(prev => {
            const updated = prev.filter((_, i) => i !== index);

            // แถวสุดท้ายต้องไม่มี conjunction เสมอ
            if (updated.length > 0) {
                const lastIndex = updated.length - 1;
                updated[lastIndex] = { ...updated[lastIndex], conjunction: '' };
            }

            return updated;
        });
    };

    const handleDeleteChip = (childIndex, indexToDelete) => {
        const currentValues = Array.isArray(subjectConstraintChild[childIndex]?.r_operand)
            ? subjectConstraintChild[childIndex].r_operand
            : [];
        const updatedValues = currentValues.filter((_, i) => i !== indexToDelete);
        handleChange(childIndex, "r_operand", updatedValues);
    };

    const handleAddChip = (childIndex, operandType) => {
        let newValue = (inputValues[childIndex] || '').trim();
        if (newValue === '') return;

        const currentValues = Array.isArray(subjectConstraintChild[childIndex]?.r_operand)
            ? subjectConstraintChild[childIndex].r_operand
            : [];
        const updatedValues = currentValues.includes(newValue)
            ? currentValues
            : [...currentValues, newValue];

        // กรณีค่าอื่นๆ
        handleChange(childIndex, "r_operand", updatedValues);
        // เคลียร์ input field ในกรณี duplicate หรือไม่
        setInputValueByIndex(childIndex, '');

        const attributeType = subjectConstraintChild[childIndex]?.l_operand?.attributeType;

        if (hasSpecialCharacters(newValue)) {
            setInputErrorByIndex(childIndex, "Special characters are not allowed");
            setValueErrorByIndex(childIndex, "Special characters are not allowed");
            return;
        } else if (!isValidType(newValue, operandType, attributeType)) {
            setInputErrorByIndex(childIndex, `Invalid ${attributeType || operandType || 'string'} value`);
            setValueErrorByIndex(childIndex, `Invalid ${attributeType || operandType || 'string'} value`);
            return;
        } else {
            setInputErrorByIndex(childIndex, null);
            setValueErrorByIndex(childIndex, null);
        }
    };

    const handleConfirm = () => {
        const subject_constraints = nodesRef.current.find(node => node.id === selectedNode.id)?.data?.general_properties?.subjectConstraints || [];
        let newSubjectConstraints = [];

        if (editingOuterIndex === null) {

            let updatedConditions = subject_constraints;
            if (subject_constraints.length > 0) {
                updatedConditions = subject_constraints.map((cond, i) => {
                    if (i === subject_constraints.length - 1) {
                        return { ...cond, conjunction: "AND" };
                    }
                    return cond;
                });
            }

            newSubjectConstraints = [...updatedConditions, { child: subjectConstraintChild, conjunction: "" }];

        } else {

            newSubjectConstraints = subject_constraints.map((cond, i) => {
                if (i !== editingOuterIndex) return cond;
                return { ...cond, child: subjectConstraintChild };
            });

        }

        const validatedSubjectConstraints = validateSubjectConstraints(newSubjectConstraints);

        let nodes_update = [];
        if (selectedNode.type !== 'subject') {
            // inherit child node
            const childs = findChildrenNode(nodesRef.current, edges, selectedNode.id);
            // console.log('childs', childs)
            nodes_update = handleUpdateChildNode(childs, validatedSubjectConstraints);
            // console.log('nodes_update', nodes_update);
        }

        setNodes((prev) =>
            prev.map((node) => {
                if (node.id === selectedNode.id) {
                    return applySubjectConstraintValidationToNode(node, validatedSubjectConstraints);
                }
                if (nodes_update.some(n => n.id === node.id)) {
                    const nodeTarget = nodes_update.find(n => n.id === node.id);
                    const nodeTargetConstraints = nodeTarget?.data?.general_properties?.subjectConstraints || [];
                    return applySubjectConstraintValidationToNode(node, nodeTargetConstraints);
                }
                return node;
            })
        );

        handleClose();
        setUpdateProperties(true);
    };

    const markConstraintsAsInherited = (constraints = []) => {
        return constraints.map((constraint) => ({
            ...constraint,
            inherited: true
        }));
    };

    const getSubjectConstraintsFromNode = (node) => {
        return node?.data?.general_properties?.subjectConstraints || [];
    };

    const updateNodeSubjectConstraints = (node, subjectConstraints) => {
        return {
            ...node,
            data: {
                ...node?.data,
                general_properties: {
                    ...node?.data?.general_properties,
                    subjectConstraints
                }
            }
        };
    };

    const mergeInheritedConstraints = (incomingConstraints = [], existingConstraints = [], { addWhenNoInherited = true } = {}) => {
        if (existingConstraints.length === 0) {
            return markConstraintsAsInherited(incomingConstraints);
        }

        const inheritedConstraints = existingConstraints.filter((item) => item.inherited === true);
        const customConstraints = existingConstraints.filter((item) => !item.inherited);

        if (inheritedConstraints.length === 0 && !addWhenNoInherited) {
            return null;
        }

        const mergedInheritedConstraints = markConstraintsAsInherited(incomingConstraints);
        const lastConjunction = inheritedConstraints.length > 0
            ? inheritedConstraints[inheritedConstraints.length - 1].conjunction
            : 'AND';

        if (mergedInheritedConstraints.length > 0) {
            mergedInheritedConstraints[mergedInheritedConstraints.length - 1] = {
                ...mergedInheritedConstraints[mergedInheritedConstraints.length - 1],
                conjunction: lastConjunction
            };
        }

        return [...mergedInheritedConstraints, ...customConstraints];
    };

    const updatePathChildrenConstraints = (parentNode, listUpdated) => {
        const pathChildIds = findChildrenNode(nodesRef.current, edges, parentNode.id);
        if (pathChildIds.length === 0) {
            return;
        }

        const inheritedConstraintsForPaths = markConstraintsAsInherited(getSubjectConstraintsFromNode(parentNode));

        for (const pathId of pathChildIds) {
            const pathNode = getNode(pathId);
            if (pathNode?.type !== 'path') {
                continue;
            }

            const existingPathConstraints = getSubjectConstraintsFromNode(pathNode);
            const mergedPathConstraints = mergeInheritedConstraints(
                inheritedConstraintsForPaths,
                existingPathConstraints,
                { addWhenNoInherited: false }
            );

            if (existingPathConstraints.length > 0 && !mergedPathConstraints) {
                continue;
            }

            const updatedPathNode = updateNodeSubjectConstraints(
                pathNode,
                mergedPathConstraints || inheritedConstraintsForPaths
            );

            listUpdated.push(updatedPathNode);
        }
    };

    const handleUpdateChildNode = (childs, subjectConstraints) => {
        const listUpdated = [];
        const inheritedConstraints = markConstraintsAsInherited(subjectConstraints);

        for (const nodeId of childs) {
            const node = getNode(nodeId);

            if (node?.type !== 'router' && node?.type !== 'path') {
                continue;
            }

            const existingConstraints = getSubjectConstraintsFromNode(node);
            const mergedConstraints = mergeInheritedConstraints(inheritedConstraints, existingConstraints);
            const updatedNode = updateNodeSubjectConstraints(node, mergedConstraints || inheritedConstraints);

            listUpdated.push(updatedNode);
            updatePathChildrenConstraints(updatedNode, listUpdated);
        }

        return listUpdated;
    };

    const renderChild = () => (
        <Box>
            {subjectConstraintChild.map((child, index) => {
                const fieldValueError = valueErrors[index];
                const fieldInputError = inputErrors[index];
                const fieldInputValue = inputValues[index] ?? '';

                return (
                    <div key={index} className='grid grid-cols-[32%_13%_30%_12%_5%] gap-4 mb-4 '>

                        <Box>
                            <Autocomplete
                                id="combo-box"
                                size='small'
                                fullWidth
                                options={subjectAttributes}
                                getOptionLabel={(option) => option.name}
                                value={child.l_operand}
                                onChange={(event, newValue) => handleChange(index, "l_operand", newValue)}
                                renderValue={(value) => (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1 }}>
                                        <span style={{
                                            textDecoration: child.attr_error_text === 'Attribute no longer available. Please select another.' ? 'line-through' : 'none',
                                            color: child.attr_error_text === 'Attribute no longer available. Please select another.' ? theme.palette.error.main : 'inherit',
                                        }}>{value?.name}</span>
                                        {value?.attributeType && (
                                            <Chip
                                                label={value.attributeType}
                                                size="small"
                                                sx={{
                                                    fontSize: '0.6rem',
                                                    textDecoration: child.attr_error_text ? 'line-through' : 'none',
                                                    color: child.attr_error_text ? 'error.main' : 'default',
                                                }}
                                            />
                                        )}
                                    </Box>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Subject Attribute"
                                        error={!!child?.attr_error_text}
                                        helperText={child?.attr_error_text || ""}
                                    />
                                )}
                                renderOption={(props, option) => {
                                    const { key, ...other } = props;
                                    return (
                                        <Box component="li" key={key} {...other} display="flex" justifyContent="space-between" alignItems="center">
                                            <span>{option.name}</span>
                                            <Chip
                                                label={option.attributeType}
                                                size="small"
                                                sx={{ fontSize: '0.6rem', ml: 1 }}
                                            />
                                        </Box>
                                    );
                                }}
                            />
                        </Box>

                        <Box>
                            <FormControl size="small"
                                fullWidth
                                disabled={!child.l_operand}
                                error={!!child?.operator_error_text}
                            >
                                <InputLabel>Operator</InputLabel>
                                <Select
                                    label="Operator"
                                    value={getOperatorConstraint(child.operator)}
                                    onChange={(e) => handleChange(index, "operator", e.target.value)}
                                    autoWidth
                                    sx={{
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                    }}
                                >
                                    {getValidConstraintOperators(child.l_operand?.operators || []).map((opt, idx) => (
                                        <MenuItem key={idx} value={getOperatorConstraint(opt)}>{getOperatorLabel(opt)}</MenuItem>
                                    ))}
                                </Select>
                                <FormHelperText>{child?.operator_error_text || ""}</FormHelperText>
                            </FormControl>
                        </Box>

                        <Box>
                            {(child?.l_operand?.type === "boolean") ? (
                                <FormControl
                                    size="small"
                                    fullWidth
                                    error={!!child?.data_type_error_text || !!fieldValueError}
                                >
                                    <InputLabel id={`select-value-label-${index}`} shrink>Value</InputLabel>
                                    <Select
                                        labelId={`select-value-label-${index}`}
                                        label="Value"
                                        value={child.r_operand}
                                        onChange={(e) => {
                                            handleChange(index, "r_operand", e.target.value)
                                        }}
                                        sx={{ justifyContent: "center", alignItems: "center" }}
                                        disabled={!child.operator}
                                    >
                                        <MenuItem value={true}>True</MenuItem>
                                        <MenuItem value={false}>False</MenuItem>
                                    </Select>
                                    <FormHelperText>{child?.data_type_error_text || fieldValueError || ""}</FormHelperText>
                                </FormControl>
                            ) : (child?.l_operand?.type === "date") ? (
                                <FormControl size="small" fullWidth>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <DatePicker
                                            key={index}
                                            format="YYYY-MM-DD"
                                            slotProps={{
                                                textField: {
                                                    label: 'Value',
                                                    size: 'small',
                                                    fullWidth: true,
                                                    InputLabelProps: {
                                                        shrink: true
                                                    },
                                                    inputProps: {
                                                        readOnly: true // ป้องกันการพิมพ์
                                                    },
                                                    error: !!child?.data_type_error_text || !!fieldValueError,
                                                    helperText: child?.data_type_error_text || fieldValueError || ""
                                                }
                                            }}
                                            disabled={!child.operator}
                                            value={child.r_operand ? dayjs(child.r_operand) : null}
                                            onChange={(newValue) => handleDateChange(index, newValue)}
                                        />
                                    </LocalizationProvider>
                                </FormControl>
                            ) : (isMultipleConstraintOperator(child?.operator, child?.l_operand?.operators) || isInOperator(child?.operator)) ? (
                                <Autocomplete
                                    id="constant-multiple-combo-box"
                                    size='small'
                                    multiple
                                    freeSolo
                                    fullWidth
                                    disabled={!child.operator}
                                    options={[]}
                                    value={Array.isArray(child.r_operand) ? child.r_operand : []}
                                    inputValue={fieldInputValue}
                                    onChange={(event, newValue, reason) => handleAutoCompleteChange(index, event, newValue, reason, child?.l_operand?.type)}
                                    onInputChange={(event, newInputValue, reason) => {
                                        setInputValueByIndex(index, newInputValue);
                                        if (reason === "input") {
                                            if (hasSpecialCharacters(newInputValue)) {
                                                setInputErrorByIndex(index, "Special characters are not allowed");
                                            } else if (!isValidType(newInputValue, child?.l_operand?.type, child?.l_operand?.attributeType)) {
                                                setInputErrorByIndex(index, `Invalid ${child?.l_operand?.attributeType || child?.l_operand?.type} value`);
                                            } else {
                                                setInputErrorByIndex(index, null);
                                            }
                                        } else if (newInputValue === '') {
                                            setInputErrorByIndex(index, null);
                                        }
                                    }}
                                    filterOptions={(options, params) => {
                                        const filtered = filter(options, params);
                                        const { inputValue } = params;
                                        const currentValues = Array.isArray(child.r_operand) ? child.r_operand : [];
                                        if (
                                            inputValue !== '' &&
                                            !options.includes(inputValue) &&
                                            !currentValues.includes(inputValue)
                                        ) {
                                            filtered.push(inputValue);
                                        }
                                        return filtered;
                                    }}
                                    renderOption={(props, option) => {
                                        if (option === fieldInputValue) {
                                            return <li {...props} key={"add"}>Add "{option}"</li>;
                                        }
                                        return <li {...props} key={"etc"}>{option}</li>;
                                    }}
                                    renderTags={(value, getTagProps) =>
                                        value.map((chip, chipIndex) => {
                                            const { key, ...tagProps } = getTagProps({ index: chipIndex });
                                            return (
                                                <Chip
                                                    key={key}
                                                    label={chip}
                                                    onDelete={() => handleDeleteChip(index, chipIndex)}
                                                    {...tagProps}
                                                />
                                            );
                                        })
                                    }
                                    renderInput={(params) => <TextField
                                        {...params}
                                        label="Value"
                                        type={child?.l_operand?.type === 'number' ? 'number' : 'string'}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter') {
                                                event.preventDefault();
                                                handleAddChip(index, child?.l_operand?.type);
                                            }
                                        }}
                                        error={!!fieldInputError || !!fieldValueError || !!child.data_type_error_text}
                                        helperText={child?.data_type_error_text || fieldInputError || fieldValueError || ""}
                                    />}
                                />
                            ) : ((child?.l_operand?.type === 'string' || child?.l_operand?.type === 'number') ? (
                                <Autocomplete
                                    freeSolo
                                    fullWidth
                                    options={singleValueSuggestions}
                                    value={child.r_operand === '' || child.r_operand === null || child.r_operand === undefined ? null : String(child.r_operand)}
                                    inputValue={child.r_operand === null || child.r_operand === undefined ? '' : String(child.r_operand)}
                                    onInputChange={(event, newInputValue) => {
                                        const normalizedValue = newInputValue === '' ? '' : newInputValue;
                                        handleChange(index, "r_operand", normalizedValue);

                                        if (normalizedValue !== '' && isSQLInjection(normalizedValue)) {
                                            setValueErrorByIndex(index, "Invalid or inappropriate input.");
                                        } else if (normalizedValue !== '' && !isValidType(normalizeSingleConstraintValue(normalizedValue), child?.l_operand?.type, child?.l_operand?.attributeType)) {
                                            setValueErrorByIndex(index, `Invalid ${child?.l_operand?.attributeType || child?.l_operand?.type} value`);
                                        } else {
                                            setValueErrorByIndex(index, null);
                                        }
                                    }}
                                    onChange={(event, newValue) => {
                                        const normalizedValue = newValue === null || newValue === undefined ? '' : String(newValue);
                                        handleChange(index, "r_operand", normalizedValue);

                                        if (normalizedValue !== '' && isSQLInjection(normalizedValue)) {
                                            setValueErrorByIndex(index, "Invalid or inappropriate input.");
                                        } else if (normalizedValue !== '' && !isValidType(normalizeSingleConstraintValue(normalizedValue), child?.l_operand?.type, child?.l_operand?.attributeType)) {
                                            setValueErrorByIndex(index, `Invalid ${child?.l_operand?.attributeType || child?.l_operand?.type} value`);
                                        } else {
                                            setValueErrorByIndex(index, null);
                                        }
                                    }}
                                    disabled={!child.operator}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={"Value"}
                                            type={'text'}
                                            variant="outlined"
                                            size='small'
                                            error={(fieldValueError || child.data_type_error_text) ? true : false}
                                            helperText={(fieldValueError || child.data_type_error_text) || ""}
                                            inputProps={{
                                                ...params.inputProps,
                                                inputMode: child?.l_operand?.type === 'number' ? 'numeric' : 'text',
                                                step: isIntegerAttributeType(child?.l_operand?.attributeType) ? 1 : 'any'
                                            }}
                                        />
                                    )}
                                />
                            ) : (
                                <TextField
                                    label={"Value"}
                                    type={child?.l_operand?.type === 'number' ? 'number' : 'text'}
                                    variant="outlined"
                                    size='small'
                                    fullWidth
                                    onChange={(e) => {
                                        handleChange(index, "r_operand", e.target.value)
                                        if (isSQLInjection(e.target.value)) {
                                            setValueErrorByIndex(index, "Invalid or inappropriate input.");
                                        } else if (!isValidType(e.target.value, child?.l_operand?.type, child?.l_operand?.attributeType)) {
                                            setValueErrorByIndex(index, `Invalid ${child?.l_operand?.attributeType || child?.l_operand?.type} value`);
                                        } else {
                                            setValueErrorByIndex(index, null);
                                        }
                                    }}
                                    disabled={!child.operator}
                                    error={(fieldValueError || child.data_type_error_text) ? true : false}
                                    helperText={(fieldValueError || child.data_type_error_text) || ""}
                                    value={child.r_operand ?? ""}
                                    inputProps={{
                                        step: isIntegerAttributeType(child?.l_operand?.attributeType) ? 1 : 'any'
                                    }}
                                />
                            ))}
                        </Box>

                        {/* Add / Conjunction Select */}
                        <Box>
                            {index === subjectConstraintChild.length - 1 ? (
                                <Button
                                    sx={{
                                        height: "40px",
                                        minWidth: "100px", // ปรับให้กว้างเท่า Select
                                        width: "100%",
                                        justifyContent: "center",
                                    }}
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={() => handleAddChild(index)}
                                >
                                    Add
                                </Button>
                            ) : (
                                <FormControl size="small" sx={{ minWidth: 100 }}>
                                    <Select
                                        value={child.conjunction}
                                        onChange={(e) => handleChange(index, "conjunction", e.target.value)}
                                    >
                                        <MenuItem value="AND">AND</MenuItem>
                                        <MenuItem value="OR">OR</MenuItem>
                                    </Select>
                                </FormControl>
                            )}
                        </Box>

                        {/* Delete Button */}
                        {
                            subjectConstraintChild.length > 1 && (
                                <Box>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        sx={{
                                            height: "40px",  // ให้สูงเท่า TextField
                                            borderRadius: "4px",
                                            minWidth: "40px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            padding: "4px",
                                            borderColor: "rgba(0, 0, 0, 0.23)", // สีขอบเหมือน TextField
                                            backgroundColor: "white",
                                            "&:hover": {
                                                color: "black", // ไอคอนสีเข้มขึ้น
                                                backgroundColor: "rgba(0, 0, 0, 0.05)", // พื้นหลังขาวแต่ทึบขึ้นเล็กน้อย
                                            },
                                            // marginLeft: "14px", // ระยะห่างจากปุ่ม Add
                                        }}
                                        onClick={() => handleRemoveChild(index)}
                                    >
                                        <DeleteIcon fontSize="small" color="action" />
                                    </Button>
                                </Box>
                            )
                        }

                    </div>
                )
            })}
        </Box >
    );

    return (
        <DialogReuse
            open={open}
            onClose={handleClose}
            fullWidth={true}
            maxWidth={"md"}
            title={"Subject Constraint"}
            content={
                <div className='min-h-[calc(100vh-600px)] pt-2 pb-2'>
                    {renderChild()}
                </div>}
            actions={
                <LoadingButton variant="contained" disabled={disabled} onClick={() => handleConfirm()}>Confirm</LoadingButton>
            }
        />
    )
}

export default SubjectConstraintDialog